import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, ArrowLeft, Eye, Edit2, Trash2, ExternalLink } from 'react-feather';
import { toast, Toaster } from 'react-hot-toast';
import { useKeycloak } from '@react-keycloak/web';
import { authenticatedFetch } from '../utils/auth';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../hooks/useLanguage';

const SchedulesPage = () => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { t, i18n } = useTranslation();
  

  // States
  const [schedules, setSchedules] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form states
  const [scheduleName, setScheduleName] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState('');
  const [frequencyType, setFrequencyType] = useState('monthly');
  const [minutesInterval, setMinutesInterval] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [stopSchedule, setStopSchedule] = useState(false);
  const [stopDate, setStopDate] = useState('');
  const [stopTime, setStopTime] = useState('');
  const [timezone, setTimezone] = useState('Asia/Tokyo');
  const [language, setLanguage] = useState('Japanese');

  const [currentLanguage, setUiLanguage] = useLanguage();

  // Clear the end date if it becomes earlier than a newly selected start date
  useEffect(() => {
    if (startDate && stopDate && stopDate < startDate) {
      setStopDate('');
      setStopTime('');
    }
  }, [startDate]);

  // Fetch schedules and dashboards on component mount
  useEffect(() => {
    if (keycloak.authenticated) {
      fetchSchedules();
      fetchDashboards();
    }
  }, [keycloak.authenticated]);

  // Function to fetch schedules
  const fetchSchedules = async () => {
    setIsLoading(true);
    try {
      const response = await authenticatedFetch('api/schedules/get-schedules',{});
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch schedules: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log("Fetched schedules:", data);
      
      // Transform the data to match our UI structure
      const transformedSchedules = data.map(schedule => {
        return {
          id: schedule.id,
          name: schedule.name,
          dashboard_id: schedule.dashboard_id,
          frequency_type: schedule.frequency_type,
          minutes_interval: schedule.minutes_interval,
          status: schedule.status,
          next_update: schedule.next_update,
          stop_date: schedule.stop_date,
          stop_time: schedule.stop_time,
          created_at: schedule.created_at,
          updated_at: schedule.updated_at
        };
      });
      
      setSchedules(transformedSchedules);
    } catch (error) {
      console.error("Error fetching schedules:", error);
      toast.error(t('schedule.errors.fetchFailed'));
      setSchedules([]); // Set to empty array if error
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions to interpret cron expressions
  

  // Function to fetch all dashboards across workspaces
  const fetchDashboards = async () => {
    try {
      const response = await authenticatedFetch('api/dashboards/get/all');
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch dashboards: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log("Fetched dashboards:", data);
      
      // Map backend field names to frontend expected names
      const mappedDashboards = data.map(dashboard => ({
        id: dashboard.id,
        name: dashboard.name || 'Untitled Dashboard',
      
      }));
      
      setDashboards(mappedDashboards);
    } catch (error) {
      console.error("Error fetching dashboards:", error);
      toast.error(t('schedule.errors.fetchDashboardsFailed'));
      setDashboards([]); // Set to empty array if error
    }
  };

  // Function to create a new schedule
  const createSchedule = async () => {
    if (!scheduleName || !selectedDashboard) {
      toast.error(t('schedule.errors.nameAndDashboardRequired'));
      return;
    }

    if (!startDate || !startTime) {
      toast.error(t('schedule.errors.startDateTimeRequired'));
      return;
    }

    if (stopSchedule && stopDate) {
      const startDateTime = new Date(`${startDate}T${startTime}`);
      const endDateTime = new Date(`${stopDate}T${stopTime || '00:00'}`);
      if (endDateTime <= startDateTime) {
        toast.error(t('schedule.errors.endBeforeStart'));
        return;
      }
    }

    try {
      // Format next_update time in the required timezone
      const nextUpdateDate = new Date(`${startDate}T${startTime}`);
      
      // Format the date manually to ensure correct format
      const nextUpdate = `${startDate}T${startTime}`;

      // Format stop time if enabled
      let formattedStopTime = null;
      if (stopSchedule && stopDate && stopTime) {
        formattedStopTime = `${stopTime}`;
      }

      // Prepare schedule data in the format required by the API
      const scheduleData = {
        name: scheduleName,
        dashboard_id: selectedDashboard,
        frequency_type: frequencyType,
        next_update: nextUpdate,
        minutes_interval: frequencyType === 'monthly' ? minutesInterval : null,
        stop_schedule: stopSchedule,
        stop_date: stopSchedule ? stopDate : null,
        stop_time: formattedStopTime,
        timezone: timezone,
        language: language
      };

      const response = await authenticatedFetch('api/schedules/create-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create schedule: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      
      if (data.message) {
        // This is a MessageResponse
        toast.success(data.message);
      } else {
        // This is a ScheduleResponse
        // Add the new schedule to the list
        setSchedules(prevSchedules => [...prevSchedules, data]);
        toast.success(t('schedule.success.created'));
      }
      
      // Reset form and close modal in both cases
      resetForm();
      setShowCreateModal(false);
      
      // Refresh schedules list to ensure we have the latest data
      fetchSchedules();
    } catch (error) {
      console.error("Error creating schedule:", error);
      toast.error(t('schedule.errors.createFailed'));
    }
  };

  // Function to delete a schedule
  const deleteSchedule = async (id) => {
    if (!window.confirm(t('schedule.confirmDelete'))) {
      return;
    }
    
    try {
      const response = await authenticatedFetch(`api/schedules/${id}`, {
        method: 'DELETE'
      });
      
      if (response.status === 404) {
        toast.error(t('schedule.errors.notFound'));
        return;
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete schedule: ${response.status} ${errorText}`);
      }
      
      toast.success(t('schedule.success.deleted'));
      
      // Refresh schedules list
      fetchSchedules();
    } catch (error) {
      console.error("Error deleting schedule:", error);
      toast.error(t('schedule.errors.deleteFailed'));
    }
  };

  // Function to reset form fields
  const resetForm = () => {
    setScheduleName('');
    setSelectedDashboard('');
    setFrequencyType('monthly');
    setMinutesInterval(1);
    setStartDate('');
    setStartTime('');
    setStopSchedule(false);
    setStopDate('');
    setStopTime('');
    setTimezone('Asia/Tokyo');
    setLanguage('Japanese');
  };

  // Function to handle frequency type change
  const handleFrequencyChange = (type) => {
    setFrequencyType(type);
    if (type === 'monthly') {
      setMinutesInterval(1);
    }
  };

  const getFrequencyLabel = (schedule) => {
    if (schedule.frequency_type === 'monthly') {
      return t('schedule.frequency.everyXMonths', { interval: schedule.minutes_interval || 1 });
    }
    if (schedule.frequency_type === 'yearly') {
      return t('schedule.frequency.yearly');
    }
    // Legacy schedules created before frequency options were reduced
    if (schedule.frequency_type === 'minutes') {
      return t('schedule.frequency.everyXMinutes', { interval: schedule.minutes_interval });
    }
    return schedule.frequency_type.charAt(0).toUpperCase() + schedule.frequency_type.slice(1);
  };

  // Function to handle month interval change
  const handleMonthIntervalChange = (e) => {
    const value = parseInt(e.target.value);
    if (value >= 1 && value <= 12) { // Allow intervals between 1 and 12 months
      setMinutesInterval(value);
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4 mx-auto"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // Find dashboard name by ID
  const getDashboardName = (dashboardId) => {
    const dashboard = dashboards.find(d => d.id === dashboardId);
    return dashboard ? dashboard.name : 'Unknown Dashboard';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{t('schedule.title')}</h1>
            <p className="text-gray-600">{t('schedule.subtitle')}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          {/* Language Switcher */}
          <LanguageSwitcher 
            currentLanguage={currentLanguage}
            onLanguageChange={setUiLanguage}
            isResearchDashboard={false}
            disabled={isLoading}
          />
          
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-blue-700"
          >
            <Plus size={18} className="mr-2" />
            {t('schedule.createButton')}
          </button>
        </div>
      </div>
      
      {/* Schedules List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.form.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.form.dashboard')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.table.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.form.updateFrequency')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.form.nextUpdate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.form.endDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.form.endTime')}</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('schedule.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {schedules.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                    {t('schedule.noSchedules')}
                  </td>
                </tr>
              ) : (
                schedules.map((schedule) => (
                  <tr key={schedule.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{schedule.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{getDashboardName(schedule.dashboard_id)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        schedule.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : schedule.status === 'COMPLETED'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {schedule.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getFrequencyLabel(schedule)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(schedule.next_update).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {schedule.stop_date || 'No end date'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {schedule.stop_time ? schedule.stop_time : 'No end time'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-center space-x-2">
                        <button
                          onClick={() => navigate(`/dashboard/${schedule.dashboard_id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Dashboard"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => deleteSchedule(schedule.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Schedule"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Create Schedule Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 shrink-0">
              <h2 className="text-xl font-bold text-gray-800">{t('schedule.modal.createTitle')}</h2>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
                aria-label={t('common.cancel')}
              >
                ✕
              </button>
            </div>
            
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-3">
              {/* Schedule Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schedule.form.name')}
                </label>
                <input
                  type="text"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder={t('schedule.form.name')}
                />
              </div>
              
              {/* Dashboard Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schedule.form.dashboard')}
                </label>
                <select
                  value={selectedDashboard}
                  onChange={(e) => setSelectedDashboard(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">{t('schedule.form.selectDashboard')}</option>
                  {dashboards.map((dashboard) => (
                    <option key={dashboard.id} value={dashboard.id}>
                      {dashboard.name}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Start From Date and Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schedule.form.startFrom')}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
              
              {/* Frequency Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schedule.form.updateFrequency')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handleFrequencyChange('monthly')}
                    className={`py-2 px-4 rounded-md ${
                      frequencyType === 'monthly' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    {t('schedule.frequency.monthly')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleFrequencyChange('yearly')}
                    className={`py-2 px-4 rounded-md ${
                      frequencyType === 'yearly' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-200 text-gray-800'
                    }`}
                  >
                    {t('schedule.frequency.yearly')}
                  </button>
                </div>
              </div>
              
              {/* Month Interval (visible only if frequencyType is 'monthly') */}
              {frequencyType === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('schedule.form.runEvery')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={minutesInterval}
                    onChange={handleMonthIntervalChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    {t('schedule.frequency.everyXMonths', { interval: minutesInterval })}
                  </p>
                </div>
              )}
              
              {/* Timezone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schedule.form.timezone')}
                </label>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                  <option value="Asia/Kolkata">Asia/Kolkata</option>
                  <option value="Asia/Shanghai">Asia/Shanghai</option>
                  <option value="Asia/Singapore">Asia/Singapore</option>
                  <option value="Asia/Dubai">Asia/Dubai</option>
                  <option value="Europe/London">Europe/London</option>
                  <option value="Europe/Paris">Europe/Paris</option>
                  <option value="Europe/Berlin">Europe/Berlin</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="America/Los_Angeles">America/Los_Angeles</option>
                  <option value="America/Chicago">America/Chicago</option>
                  <option value="Australia/Sydney">Australia/Sydney</option>
                  <option value="Pacific/Auckland">Pacific/Auckland</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              {/* Language Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schedule.form.language')}
                </label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="Japanese">{t('languageSwitcher.japanese')}</option>
                  <option value="English">{t('languageSwitcher.english')}</option>
                </select>
              </div>
              
              {/* End Date Option */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="stopSchedule"
                  checked={stopSchedule}
                  onChange={() => setStopSchedule(!stopSchedule)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label htmlFor="stopSchedule" className="ml-2 text-sm text-gray-700">
                  {t('schedule.form.stopSchedule')}
                </label>
              </div>
              
              {/* End Date and Time (visible only if stopSchedule is true) */}
              {stopSchedule && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('schedule.form.endDate')}
                    </label>
                    <input
                      type="date"
                      value={stopDate}
                      min={startDate || undefined}
                      onChange={(e) => setStopDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('schedule.form.endTime')}
                    </label>
                    <input
                      type="time"
                      value={stopTime}
                      onChange={(e) => setStopTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-2 px-6 py-4 border-t border-gray-200 shrink-0 bg-white rounded-b-lg">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={createSchedule}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulesPage; 